import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { LayoutAdminCafe } from "../../../layout/layout_cafe";
import { UserResponse } from "../../../interfaces/user";
import {
  ChangeRolesUserBody,
  adminResetPassword,
  deleteUserByID,
  getUserByID,
  getUserMe,
  patchUserByID,
} from "../../../api/user.api";
import { getLocalStorage } from "../../../api/axios";
import { loadSettingsCached } from "../../../api/settings.api";
import { UserAvatar } from "../../../avatar/UserAvatar";

const ROLES: { id: "user" | "barista" | "admin"; label: string; tone: string }[] = [
  { id: "user",    label: "User",    tone: "bg-ink-100 text-ink-1000" },
  { id: "barista", label: "Barista", tone: "bg-amber-50 text-amber-900" },
  { id: "admin",   label: "Admin",   tone: "bg-violet-50 text-violet-900" },
];

const EditUserInner: React.FC = () => {
  const { id } = useParams();
  const userId = id ? Number(id) : 0;
  const navigate = useNavigate();

  const [me, setMe] = useState<UserResponse | null>(null);
  const [original, setOriginal] = useState<UserResponse | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"user" | "barista" | "admin">("user");
  const [isAvailable, setIsAvailable] = useState(true);
  const [birthday, setBirthday] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Per-user daily limit override (dev-only).
  const [globalLimit, setGlobalLimit] = useState<number>(2);
  const [useCustomLimit, setUseCustomLimit] = useState(false);
  const [customLimit, setCustomLimit] = useState<number>(2);

  // password reset
  const [showPwd, setShowPwd] = useState(false);
  const [newPwd, setNewPwd] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);

  // delete confirm
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [u, current] = await Promise.all([
          getUserByID(userId),
          getUserMe({ access_token: getLocalStorage("access_token").now_token ?? "" })
            .catch(() => null),
        ]);
        if (cancelled) return;
        setOriginal(u);
        setName(u.name);
        setEmail(u.email);
        setRole((u.role as any) === "dev" ? "admin" : (u.role as any));
        setIsAvailable(u.isAvailable);
        setBirthday(u.birthday ? moment(u.birthday).format("YYYY-MM-DD") : "");
        setMe(current);
        if (typeof u.dailyOrderLimit === "number") {
          setUseCustomLimit(true);
          setCustomLimit(u.dailyOrderLimit);
        } else {
          setUseCustomLimit(false);
        }
        loadSettingsCached()
          .then((s) => {
            setGlobalLimit(s.dailyOrderLimit);
            // Seed custom field with global so the +/- starts somewhere sensible.
            if (typeof u.dailyOrderLimit !== "number") setCustomLimit(s.dailyOrderLimit);
          })
          .catch(() => {});
      } catch (err) {
        console.error(err);
        toast.error("Could not load user");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const isMe = me && original && me.id === original.id;
  const isDev = original?.role === "dev";
  const editorIsDev = me?.role === "dev";

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!email.trim()) { toast.error("Email is required"); return; }
    setSaving(true);
    try {
      const body: ChangeRolesUserBody = {
        name: name.trim(),
        email: email.trim(),
        isAvailable,
        birthday,
      };
      // Don't downgrade dev role; don't let user demote themselves out of admin.
      if (!isDev && !isMe) body.role = role;
      // Only dev can change the per-user daily limit. Server enforces this too.
      if (editorIsDev) {
        body.dailyOrderLimit = useCustomLimit ? customLimit : null;
      }
      await patchUserByID(userId, body);
      toast.success("Saved");
      navigate(-1);
    } catch (err) {
      console.error(err);
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPwd) { toast.error("Please enter a new password"); return; }
    setPwdSaving(true);
    try {
      await adminResetPassword(userId, newPwd);
      toast.success("Password reset");
      setShowPwd(false);
      setNewPwd("");
    } catch (err) {
      console.error(err);
      toast.error("Could not reset password");
    } finally {
      setPwdSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteUserByID(userId);
      toast.success("User deleted");
      navigate("/userManagement", { replace: true });
    } catch (err) {
      console.error(err);
      toast.error("Could not delete");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <svg className="w-6 h-6 animate-spin text-ink-700" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
          <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>
    );
  }

  if (!original) {
    return (
      <div className="min-h-screen bg-white px-5 pt-[40px] text-center">
        <p className="font-display font-black text-lg text-ink-1000">User not found</p>
        <button onClick={() => navigate(-1)} className="btn-v2-secondary btn-v2-sm mt-4">Back</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-sans pb-[120px]">
      <header className="px-5 pt-[24px] pb-[16px] border-b border-ink-100 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="w-[40px] h-[40px] rounded-full flex items-center justify-center text-ink-1000 hover:bg-ink-100"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="font-display font-black text-[22px] leading-tight text-ink-1000">Edit user</h1>
      </header>

      {/* Avatar + identity */}
      <div className="px-5 pt-[20px] flex items-center gap-4">
        <UserAvatar avatar={original.avatar} name={original.name} size={60} />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-ink-500">User ID #{original.id}</p>
          <p className="font-display font-bold text-base text-ink-1000 truncate">{original.email}</p>
          {original.createdAt && (
            <p className="text-[11px] text-ink-400">Joined {moment(original.createdAt).format("D MMM YYYY")}</p>
          )}
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="px-5 pt-[20px] space-y-[20px]">
        {/* Name */}
        <section>
          <span className="block font-display font-bold text-sm text-ink-1000 mb-2">
            Name <span className="text-red-500">*</span>
          </span>
          <input
            type="text"
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            className="w-full h-[52px] px-4 rounded-2xl bg-ink-50 border-2 border-transparent focus:border-ink-1000 focus:bg-white focus:outline-none text-base text-ink-1000"
          />
        </section>

        {/* Email */}
        <section>
          <span className="block font-display font-bold text-sm text-ink-1000 mb-2">
            Email <span className="text-red-500">*</span>
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-[52px] px-4 rounded-2xl bg-ink-50 border-2 border-transparent focus:border-ink-1000 focus:bg-white focus:outline-none text-base text-ink-1000"
          />
        </section>

        {/* Birthday */}
        <section>
          <span className="block font-display font-bold text-sm text-ink-1000 mb-2">Birthday</span>
          <input
            type="date"
            value={birthday}
            max={moment().format("YYYY-MM-DD")}
            onChange={(e) => setBirthday(e.target.value)}
            className="w-full h-[52px] px-4 rounded-2xl bg-ink-50 border-2 border-transparent focus:border-ink-1000 focus:bg-white focus:outline-none text-base text-ink-1000"
          />
          <p className="text-xs text-ink-500 mt-1.5">Optional · used for the year-end Wrapped recap</p>
        </section>

        {/* Role */}
        <section>
          <span className="block font-display font-bold text-sm text-ink-1000 mb-2">Role</span>
          {isDev ? (
            <div className="px-4 h-[52px] rounded-2xl bg-emerald-50 flex items-center justify-between">
              <span className="font-display font-bold text-sm text-emerald-900">Developer</span>
              <span className="text-xs text-emerald-700">Read-only</span>
            </div>
          ) : isMe ? (
            <div className="px-4 h-[52px] rounded-2xl bg-ink-50 flex items-center justify-between">
              <span className="font-display font-bold text-sm text-ink-1000 capitalize">{role}</span>
              <span className="text-xs text-ink-500">You can't change your own role</span>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {ROLES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRole(r.id)}
                  className={`h-[52px] rounded-2xl font-display font-bold text-sm border-2 transition ${
                    role === r.id
                      ? "bg-ink-1000 text-ink-0 border-ink-1000"
                      : "bg-ink-50 text-ink-700 border-transparent hover:border-ink-200"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Active toggle */}
        <section>
          <div className="flex items-center justify-between gap-3 px-4 h-[52px] rounded-2xl bg-ink-50">
            <div className="flex-1 min-w-0">
              <span className="block font-display font-bold text-sm text-ink-1000">Account active</span>
              <span className="text-xs text-ink-500">Inactive accounts can't log in</span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={isAvailable}
                onChange={(e) => setIsAvailable(e.target.checked)}
                disabled={isMe ?? false}
              />
              <div className="w-11 h-6 bg-ink-300 rounded-full peer-checked:bg-emerald-500 transition-colors peer-disabled:opacity-50 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
            </label>
          </div>
        </section>

        {/* Per-user daily order limit (dev only) */}
        {editorIsDev && (
          <section>
            <span className="block font-display font-bold text-sm text-ink-1000 mb-2">
              Daily order limit
            </span>
            <div className="rounded-2xl bg-ink-50 p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <span className="block text-sm font-bold text-ink-1000">Custom limit for this user</span>
                  <span className="text-xs text-ink-500">
                    Off = follow the global setting ({globalLimit}/day)
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={useCustomLimit}
                    onChange={(e) => setUseCustomLimit(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-ink-300 rounded-full peer-checked:bg-ink-1000 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
                </label>
              </div>

              {useCustomLimit && (
                <div className="pt-3 border-t border-ink-200">
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={50}
                      value={customLimit}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setCustomLimit(Number.isNaN(v) ? 0 : v);
                      }}
                      className="flex text-center w-18 h-30 px-5 rounded-2xl bg-ink-50 border-2 border-transparent focus:border-ink-1000 focus:bg-white focus:outline-none font-display font-black text-3xl text-ink-1000 transition-colors"
                    />
                    <span className="flex-1 text-sm font-semibold text-center text-ink-500 whitespace-nowrap">cups per day</span>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Reset password */}
        <section>
          <span className="block font-display font-bold text-sm text-ink-1000 mb-2">Password</span>
          {showPwd ? (
            <div className="space-y-2">
              <input
                type="text"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="New password (up to 20 chars)"
                aria-label="New password"
                maxLength={20}
                className="w-full h-[52px] px-4 rounded-2xl bg-ink-50 border-2 border-transparent focus:border-ink-1000 focus:bg-white focus:outline-none text-base text-ink-1000"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setShowPwd(false); setNewPwd(""); }}
                  className="btn-v2 flex-1 h-[44px] bg-ink-100 text-ink-1000 rounded-2xl"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={pwdSaving || !newPwd}
                  className="btn-v2-primary flex-1 h-[44px] rounded-2xl"
                >
                  {pwdSaving ? "Saving…" : "Set new password"}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowPwd(true)}
              className="w-full h-[52px] rounded-2xl bg-ink-50 hover:bg-ink-100 text-ink-1000 font-display font-bold text-sm flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
              </svg>
              Reset password
            </button>
          )}
        </section>

        {/* Delete (admin can soft-delete others, not self / dev) */}
        {!isMe && !isDev && (
          <section>
            <span className="block font-display font-bold text-sm text-ink-1000 mb-2">Danger zone</span>
            <button
              type="button"
              onClick={() => setShowDelete(true)}
              className="w-full h-[52px] rounded-2xl bg-red-50 hover:bg-red-100 text-red-700 font-display font-bold text-sm flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Delete account
            </button>
          </section>
        )}
      </form>

      {/* Sticky save footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-ink-100 px-5 py-3 flex gap-2 md:pl-[300px]">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="btn-v2 flex-1 h-[48px] bg-ink-100 text-ink-1000 rounded-2xl"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="btn-v2-primary flex-1 h-[48px] rounded-2xl"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Delete confirm modal */}
      {showDelete && (
        <div className="fixed inset-0 z-[60] bg-ink-1000/50 backdrop-blur-sm flex items-center justify-center px-5">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-floaty">
            <h3 className="font-display font-black text-xl text-ink-1000 mb-2">Delete this account?</h3>
            <p className="text-sm text-ink-500 mb-5">
              <span className="font-bold text-ink-1000">{original.name}</span> ({original.email}) will be soft-deleted. They won't be able to log in. This can be reversed by support.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowDelete(false)}
                className="btn-v2 flex-1 h-[44px] bg-ink-100 text-ink-1000 rounded-2xl"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="btn-v2 flex-1 h-[44px] bg-red-600 text-white rounded-2xl"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export const EditUser: React.FC = () => (
  <LayoutAdminCafe>
    <EditUserInner />
  </LayoutAdminCafe>
);

export default EditUser;
